import React, { useState, useMemo, useRef, useEffect } from "react";
import { toast } from "sonner";
import { X, ClipboardCopy, MessageCircle, ListChecks, Loader2 } from "lucide-react";
import { api } from "../lib/api";

/**
 * Modal — Gerar lista de cobrança por indicador (iter54).
 *
 * Props:
 *   • indicator: {id, name, whatsapp?, phone?} — indicador selecionado
 *   • items: linhas selecionadas [{client_id, client_name, due_date, bucket: 'atrasados'|'hoje'}]
 *   • onClose
 *
 * Formato de saída:
 *   PENDÊNCIAS DE HOJE
 *
 *   CLIENTE X
 *   Venceu em: DD/MM/AAAA
 *
 *   CLIENTE Y
 *   Vence hoje: DD/MM/AAAA
 */

function _formatDateBR(iso) {
  if (!iso) return "";
  try {
    const [y, m, d] = iso.slice(0, 10).split("-");
    return `${d}/${m}/${y}`;
  } catch (_) { return iso; }
}

function _normalizeBRPhone(raw) {
  if (!raw) return null;
  const digits = String(raw).replace(/\D+/g, "");
  if (!digits) return null;
  // Se já começa com 55, mantém. Senão, prefixa 55.
  return digits.startsWith("55") ? digits : `55${digits}`;
}

function _buildListText(items) {
  // Agrupa por indicador. Formato:
  //   PENDÊNCIAS DE HOJE
  //
  //   INDICADOR: <NOME>
  //   CLIENTE
  //   Vence hoje: dd/mm/aaaa   |   Venceu em: dd/mm/aaaa
  //   [Tipo: Parcelado · Parc. 2/6]  (só se não for contract)
  //
  //   ...
  //
  //   [2 linhas em branco]
  //   INDICADOR: <OUTRO>
  const byIndicator = new Map();
  items.forEach(it => {
    const key = (it.indicator_name || "SEM INDICADOR").toUpperCase();
    if (!byIndicator.has(key)) byIndicator.set(key, []);
    byIndicator.get(key).push(it);
  });

  const lines = ["PENDÊNCIAS DE HOJE", ""];
  const groupKeys = Array.from(byIndicator.keys()).sort();
  groupKeys.forEach((groupName, idx) => {
    lines.push(`INDICADOR: ${groupName}`);
    const rows = byIndicator.get(groupName);
    rows.forEach(it => {
      lines.push((it.client_name || "").trim().toUpperCase());
      const kind = it.kind_label && it.kind_label !== "Contrato" ? ` · ${it.kind_label}` : "";
      const parc = it.installment_number ? ` · Parcela ${it.installment_number}/${it.installment_total || "—"}` : "";
      const label = it.bucket === "hoje" ? "Vence hoje" : "Venceu em";
      lines.push(`${label}: ${_formatDateBR(it.due_date)}${kind}${parc}`);
      lines.push(""); // linha em branco entre clientes
    });
    if (idx < groupKeys.length - 1) {
      // 2 linhas em branco entre grupos
      lines.push("");
    }
  });
  return lines.join("\n").trimEnd();
}

export default function IndicatorListModal({ indicator, indicators, items, onClose }) {
  // indicator: legado (single) — se vier, indicators = [indicator]
  // indicators: novo (array) — Sprint pós-V1: seleção múltipla
  const indicatorList = indicators && indicators.length > 0
    ? indicators
    : (indicator ? [indicator] : []);
  const isMulti = indicatorList.length > 1;
  const singleIndicator = indicatorList.length === 1 ? indicatorList[0] : null;

  const [text, setText] = useState(() => _buildListText(items));
  const [busyWa, setBusyWa] = useState(false);
  const [indicatorPhone, setIndicatorPhone] = useState(
    singleIndicator ? (singleIndicator.whatsapp || singleIndicator.phone) : null,
  );
  const textareaRef = useRef(null);

  // Se o objeto indicator veio sem telefone (edge case), tenta detalhe.
  useEffect(() => {
    if (indicatorPhone || !singleIndicator?.id) return;
    api.get(`/indicators/${singleIndicator.id}`).then(r => {
      const p = r.data?.whatsapp || r.data?.phone;
      if (p) setIndicatorPhone(p);
    }).catch(() => {});
  }, [singleIndicator, indicatorPhone]);

  const copyList = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // fallback
        textareaRef.current?.select();
        document.execCommand("copy");
      }
      toast.success("Lista copiada com sucesso");
    } catch (e) {
      toast.error("Falha ao copiar. Selecione manualmente.");
    }
  };

  const openWhatsApp = () => {
    setBusyWa(true);
    try {
      const phone = _normalizeBRPhone(indicatorPhone);
      if (!phone) {
        toast.error("Indicador sem telefone cadastrado — não é possível abrir o WhatsApp");
        return;
      }
      // Abre WhatsApp sem enviar automaticamente (só pré-preenche a mensagem)
      const url = `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
      window.open(url, "_blank", "noopener,noreferrer");
    } finally {
      setTimeout(() => setBusyWa(false), 400);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="indicator-list-modal">
      <div className="w-full max-w-2xl rounded-lg border border-[#262626] bg-[#0d0d0d] shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#1f1f1f]">
          <h3 className="font-display text-base flex items-center gap-2 text-[#D4AF37]">
            <ListChecks size={16}/>
            Lista para {isMulti ? "indicadores" : "indicador"}
            <span className="text-[#737373] font-normal ml-2">
              — {isMulti ? `${indicatorList.length} selecionados` : (singleIndicator?.name || "—")}
            </span>
          </h3>
          <button onClick={onClose} className="text-[#737373] hover:text-white" data-testid="indicator-list-close">
            <X size={18}/>
          </button>
        </div>

        <div className="px-5 py-4 space-y-3 overflow-y-auto flex-1">
          <div className="rounded-md border border-[#262626] bg-[#0a0a0a] p-3 text-[12px] text-[#A3A3A3]">
            <strong className="text-[#D4AF37]">{items.length}</strong> cliente(s) selecionado(s).
            Você pode editar o texto abaixo antes de copiar ou abrir o WhatsApp do indicador.
            A mensagem NÃO é enviada automaticamente — o WhatsApp abre com o texto pré-preenchido para você conferir e enviar manualmente.
          </div>

          <textarea
            ref={textareaRef}
            className="fc-input font-mono-tab text-[13px] min-h-[300px] leading-[1.5] resize-y whitespace-pre"
            value={text}
            onChange={e => setText(e.target.value)}
            data-testid="indicator-list-textarea"
            spellCheck={false}
          />
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#1f1f1f] bg-[#0a0a0a]/50 flex-shrink-0">
          <button onClick={onClose} className="fc-btn-ghost text-sm">Fechar</button>
          <button
            onClick={copyList}
            className="fc-btn-ghost flex items-center gap-2 text-sm text-[#D4AF37] border-[#D4AF37]/40"
            data-testid="indicator-list-copy"
          >
            <ClipboardCopy size={14}/> Copiar lista completa
          </button>
          {!isMulti && singleIndicator && (
            <button
              onClick={openWhatsApp}
              disabled={busyWa}
              className="fc-btn-gold flex items-center gap-2 text-sm"
              data-testid="indicator-list-whatsapp"
              title={indicatorPhone ? `Abrir WhatsApp de ${singleIndicator.name}` : "Indicador sem telefone"}
            >
              {busyWa ? <Loader2 size={14} className="animate-spin"/> : <MessageCircle size={14}/>}
              Abrir WhatsApp do indicador
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Exporta helpers para uso em testes/reuso
export { _buildListText as buildListText, _normalizeBRPhone as normalizeBRPhone };
